// Pure aggregation over the audit report's CSV rows, so the overview cards can be
// unit-tested without rendering. Column positions are resolved from the header row;
// the whole overview is null for CSVs without the audit columns.

export interface CurrencyBreakdown {
  coin: string;
  /** Sum of holdings in the coin's own unit (not EUR). */
  total: number;
  holders: number;
}

export interface ReportOverview {
  userCount: number;
  /** Sum of the totalEur column; null when the CSV has no such column. */
  totalEur: number | null;
  /** Sum of totalEur over OTM rows; null without customerType + totalEur columns. */
  otmTotalEur: number | null;
  /** Sum of fiatEur over OTE rows; null without customerType + fiatEur columns. */
  oteFiatEur: number | null;
  /** Sum of cryptoEur over OTE rows; null without customerType + cryptoEur columns. */
  oteCryptoEur: number | null;
  currencies: CurrencyBreakdown[];
}

const normalise = (header: string): string => header.trim().toLowerCase();

/** Numeric value of a CSV cell, or 0 for blanks and junk — sums stay finite whatever the data. */
const cellNumber = (cell: string | undefined): number => {
  const value = Number(cell);
  return cell?.trim() && Number.isFinite(value) ? value : 0;
};

/** Split a `COIN:amount|COIN:amount` balance blob into [coin, amount] pairs. */
export const parseAssetEntries = (blob: string): Array<[string, number]> => {
  if (!blob) return [];
  return blob.split('|').flatMap((entry) => {
    const [coin, amount = ''] = entry.split(':');
    if (!coin) return [];
    return [[coin, Number(amount)] as [string, number]];
  });
};

export const computeReportOverview = (headers: string[], rows: string[][]): ReportOverview | null => {
  const normalised = headers.map(normalise);
  const totalEurIndex = normalised.indexOf('totaleur');
  const assetsIndex = normalised.indexOf('assets');
  if (totalEurIndex === -1 && assetsIndex === -1) return null;

  const customerTypeIndex = normalised.indexOf('customertype');
  const fiatEurIndex = normalised.indexOf('fiateur');
  const cryptoEurIndex = normalised.indexOf('cryptoeur');

  let totalEur = 0;
  let otmTotalEur = 0;
  let oteFiatEur = 0;
  let oteCryptoEur = 0;
  const byCoin = new Map<string, CurrencyBreakdown>();

  for (const row of rows) {
    if (totalEurIndex !== -1) totalEur += cellNumber(row[totalEurIndex]);

    const customerType = customerTypeIndex === -1 ? '' : (row[customerTypeIndex] ?? '').trim().toUpperCase();
    if (customerType === 'OTM' && totalEurIndex !== -1) otmTotalEur += cellNumber(row[totalEurIndex]);
    if (customerType === 'OTE') {
      if (fiatEurIndex !== -1) oteFiatEur += cellNumber(row[fiatEurIndex]);
      if (cryptoEurIndex !== -1) oteCryptoEur += cellNumber(row[cryptoEurIndex]);
    }

    if (assetsIndex !== -1) {
      for (const [coin, amount] of parseAssetEntries(row[assetsIndex] ?? '')) {
        const existing = byCoin.get(coin) ?? { coin, total: 0, holders: 0 };
        byCoin.set(coin, {
          coin,
          total: existing.total + (Number.isFinite(amount) ? amount : 0),
          holders: existing.holders + 1,
        });
      }
    }
  }

  const currencies = [...byCoin.values()].sort((a, b) => b.holders - a.holders || a.coin.localeCompare(b.coin));
  return {
    userCount: rows.length,
    totalEur: totalEurIndex === -1 ? null : totalEur,
    otmTotalEur: customerTypeIndex === -1 || totalEurIndex === -1 ? null : otmTotalEur,
    oteFiatEur: customerTypeIndex === -1 || fiatEurIndex === -1 ? null : oteFiatEur,
    oteCryptoEur: customerTypeIndex === -1 || cryptoEurIndex === -1 ? null : oteCryptoEur,
    currencies,
  };
};
